import axios from 'axios'

// 基础配置
const BASE_URL = 'https://hjm.xyhy.live'
const WS_BASE_URL = 'wss://hjm.xyhy.live' // wss://stargame-api.xyhy.live

// 创建axios实例
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // 10秒超时
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器 - 添加ticket等通用处理
apiClient.interceptors.request.use(
  (config) => {
    // 🎫 获取ticket而不是token
    const ticket = localStorage.getItem('ticket') || sessionStorage.getItem('ticket')
    console.log('🎫 获取到的ticket:', ticket ? `${ticket.substring(0, 20)}...` : '未找到ticket')

    if (ticket) {
      // 🔧 根据API要求设置Authorization格式
      // 常见格式有以下几种，你可能需要根据实际API调整：

      // 格式1: Bearer ticket (类似token)
      config.headers.Authorization = `Bearer ${ticket}`

      // 格式2: 直接使用ticket
      // config.headers.Authorization = ticket

      // 格式3: Ticket 前缀
      // config.headers.Authorization = `Ticket ${ticket}`

      console.log('✅ 已添加Authorization header (ticket)')
    } else {
      console.warn('⚠️ 未找到ticket，请求可能会失败')
    }

    console.log(
      '🚀 API请求:',
      config.method?.toUpperCase(),
      config.url,
      config.headers.Authorization ? '已认证' : '未认证',
    )
    return config
  },
  (error) => {
    console.error('❌ 请求拦截器错误:', error)
    return Promise.reject(error)
  },
)

// 响应拦截器 - 统一处理响应和错误
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API响应:', response.status, response.config.url)
    return response
  },
  (error) => {
    console.error('❌ API错误:', error.response?.status, error.config?.url, error.response?.data)

    // 🎫 统一错误处理 - ticket相关
    if (error.response?.status === 401) {
      console.error('🔐 认证失败，可能需要重新获取ticket')

      // 清除可能过期的ticket
      //localStorage.removeItem('ticket')
      //sessionStorage.removeItem('ticket')

      // 触发重新认证事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('ticketAuthenticationRequired', {
            detail: { reason: 'ticket_expired', originalError: error },
          }),
        )
      }
    }

    return Promise.reject(error)
  },
)

// 通用API方法
const api = {
  // GET请求
  get: (url, config = {}) => {
    return apiClient.get(url, config)
  },

  // POST请求
  post: (url, data = {}, config = {}) => {
    return apiClient.post(url, data, config)
  },

  // PUT请求
  put: (url, data = {}, config = {}) => {
    return apiClient.put(url, data, config)
  },

  // DELETE请求
  delete: (url, config = {}) => {
    return apiClient.delete(url, config)
  },

  // PATCH请求
  patch: (url, data = {}, config = {}) => {
    return apiClient.patch(url, data, config)
  },
}

// WebSocket连接方法
const createWebSocketUrl = (endpoint, params = {}) => {
  const url = new URL(endpoint, WS_BASE_URL)

  // 添加查询参数
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null) {
      url.searchParams.append(key, params[key])
    }
  })

  return url.toString()
}

// 具体的API接口封装
const endpoints = {
  // 用户相关
  user: {
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    profile: () => api.get('/user/profile'),
    updateProfile: (data) => api.put('/user/profile', data),
    // 🔍 用户搜索接口 - 支持多字段搜索（昵称、用户名、TikTok ID）
    search: (keyword) => {
      if (!keyword || keyword.trim().length < 1 || keyword.trim().length > 50) {
        return Promise.reject(new Error('搜索关键词长度必须在1-50字符之间'))
      }
      return api.get('/api/users/search', {
        params: { keyword: keyword.trim() },
      })
    },
  },

  // 咨询相关
  consult: {
    getList: (params) => api.get('/consult/list', { params }),
    getDetail: (id) => api.get(`/consult/${id}`),
    create: (data) => api.post('/consult', data),
    update: (id, data) => api.put(`/consult/${id}`, data),
    delete: (id) => api.delete(`/consult/${id}`),
    getLabels: () => api.get('/consult/labels'),
  },

  // 游戏相关
  game: {
    getList: (params) => api.get('/games', { params }),
    getDetail: (id) => api.get(`/games/${id}`),
  },

  // 🔍 聊天消息相关
  chat: {
    // 获取聊天历史记录
    getHistory: (targetUserId, limit = 50, offset = 0) => {
      if (!targetUserId) {
        return Promise.reject(new Error('目标用户ID不能为空'))
      }
      return api.get('/api/messages', {
        params: {
          to_user_id: targetUserId,
          limit: limit,
          offset: offset,
        },
      })
    },
    // 发送消息
    sendMessage: (targetUserId, content, messageType = 'text') => {
      if (!targetUserId || !content) {
        return Promise.reject(new Error('目标用户ID和消息内容不能为空'))
      }
      return api.post('/api/messages', {
        to_user_id: targetUserId,
        content: content,
        message_type: messageType,
      })
    },
    // 获取会话列表
    getConversations: (limit = 20, offset = 0) => {
      return api.get('/api/conversations', {
        params: { limit, offset },
      })
    },
  },

  // WebSocket连接
  websocket: {
    chat: (ticket) =>
      createWebSocketUrl('/ws', {
        ticket: ticket,
      }),
    notifications: (token) =>
      createWebSocketUrl('/ws/notifications', {
        token,
      }),
  },
}

// 导出配置和方法
export { BASE_URL, WS_BASE_URL, api, apiClient, endpoints, createWebSocketUrl }

// 默认导出
export default {
  BASE_URL,
  WS_BASE_URL,
  api,
  endpoints,
  createWebSocketUrl,
}
